import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { t } from "@bookingcom/lingojs-core";
import { I18nChildContext, useI18n } from "@bookingcom/lingojs-react";

import FlightCardPrice from "./FlightCardPrice";
import { Button, Card, Icon, Stack } from "@bookingcom/bui-react";
import { FlightBadgeVariant, UIFlightData, UIOrder, UITripRebookOption } from "@flights/types";

import BaseFareAttributes from "./BaseFareAttributes";
import FlightCardLuggage from "./FlightCardLuggage";

import useLocaleContext from "hooks/useLocaleContext";
import { trackExperiment } from "utils/et";
import { SBSearchType } from "@bookingcom/flights-searchbox";
import { ArrowNavDownIcon, ArrowNavUpIcon } from "@bookingcom/bui-assets-react/streamline/index.js";
import { FareSelectorDesktop } from "branded-fares/components/FareSelector/components/FareSelector.desktop";
import useIsInViewport from "hooks/useIsInViewport";
import useFetchBrandedFares from "branded-fares/hooks/useFetchBrandedFares";
import { RebookCostBreakdown } from "components/elements/RebookFlow/RebookCostBreakdown/RebookCostBreakdown";
import { FlightCardHighlight, FlightCardHighlightStyle } from "./FlightCardHighlight";
import { FlightCardBadges } from "./FlightCardBadges";
import { FlightCardBadge } from "./FlightCardBadge";
import Frame, { FrameProps } from "components/elements/Frame";
import { getCampaignBadgesConfig, getCampaignBestOfferHighlightConfig } from "utils/flightCampaignConfig";
import styles from "./FlightCardBound.desktop.module.css";
import flights_apex_web_ancillaries_micro_conversion_aa from "utils/experiments/apex/flights_apex_web_ancillaries_micro_conversion_aa";
import { isOfMetaOrigin } from "utils/marketing-url-params";
import flights_apex_web_compact_bf_on_sr_button from "utils/experiments/apex/flights_apex_web_compact_bf_on_sr_button";
import { carrierUtils, isFeatureRunningClientSide } from "@flights/web-api-utils-universal";
import useGlobalContext from "hooks/useGlobalContext";
import flights_apex_web_bf_on_sr_loading_message from "utils/experiments/apex/flights_apex_web_bf_on_sr_loading_message";
import { useSelector } from "react-redux";
import { getSearchCriteria } from "store/searchCriteria/selectors";
import { getSearchDisplayOptions, getSearchResultsFlights } from "store/searchResults/selectors";
import flights_apex_web_bf_on_sr_flex_badge from "utils/experiments/apex/flights_apex_web_bf_on_sr_flex_badge";
import FlightCardLuggageWithCheckmarks from "./elements/FlightCardLuggageWithCheckmarks/FlightCardLuggageWithCheckmarks";
import { CardSegmentsCondensed } from "./elements/CardSegmentCondensed/CardSegmentsCondensed";
import flights_web_flexible_ticket_icon, {
  useTrackFlexIcon
} from "utils/experiments/funnel/flights_web_flexible_ticket_icon";
import { mcn } from "utils/mergeClassnames";
import FlightCardPriceOutbound from "./FlightCardPrice.outbound";

export type FlightCardBoundProps = {
  flight: UIFlightData;
  onClick: (flight: UIFlightData) => void;
  pricePerPerson?: boolean;
  index: number;
  bigLogo?: boolean;
  searchType?: SBSearchType;
  rebookProps?: {
    tripRebookOption: UITripRebookOption;
    initialOrder: UIOrder;
    align: "start" | "end";
  };
  mt?: FrameProps["mt"];
  mb?: FrameProps["mb"];
  /* start - flights_alternative_fares_for_sold_out_errors_web */
  isAlternativeFareCard?: boolean;
  forceHideLastAvailableSeats?: boolean;
  /* end - flights_alternative_fares_for_sold_out_errors_web */
  /* start - flights_split_bounds_us */
  boundsVariant?: "outbound" | "inbound";
  /* end - flights_split_bounds_us */
};

const FlightCardBound: FC<FlightCardBoundProps> = (props) => {
  const { flight, onClick, pricePerPerson, index, bigLogo, rebookProps, boundsVariant } = props;
  const i18n = useI18n() as I18nChildContext;
  const { isRTL } = useLocaleContext();
  const { isInternal } = useGlobalContext();
  const flightCardBoundRef = useRef<HTMLDivElement>(null);
  const isInViewport = useIsInViewport(flightCardBoundRef.current);
  const { adults, children } = useSelector(getSearchCriteria);
  const displayOptions = useSelector(getSearchDisplayOptions);
  const campaignBadges = getCampaignBadgesConfig(flight.campaignDisplay?.badges);
  const carriers = carrierUtils.getMarketingCarriers(flight.segments);

  const showFlexBadgeOnBrandedFare =
    flight.ancillaries?.flexibleTicket &&
    flight.requestableBrandedFares &&
    !!flights_apex_web_bf_on_sr_flex_badge.trackWithDefaultStage();

  const hasFlexibleTicketBadge =
    boundsVariant !== "outbound" && flight.ancillaries?.flexibleTicket && !flight.requestableBrandedFares;

  const showFlightCardBadges =
    flight?.badges?.length ||
    campaignBadges.length > 0 ||
    hasFlexibleTicketBadge ||
    showFlexBadgeOnBrandedFare ||
    flight.hasAppleCardBenefits;

  useEffect(() => {
    if (flight.requestableBrandedFares && flight.ancillaries?.flexibleTicket) {
      flights_apex_web_bf_on_sr_flex_badge.stages.ddot();
      flights_apex_web_bf_on_sr_flex_badge.stages.search_results();
    }
  }, [flight.ancillaries?.flexibleTicket, flight.brandedFareOffers, flight.requestableBrandedFares]);

  const { bestOfferHighlightStyle, bestOfferHighlightText } = getCampaignBestOfferHighlightConfig(
    flight.campaignDisplay?.bestOfferHighlight
  );
  const shouldShowBestOfferHightlight = !!(bestOfferHighlightStyle && bestOfferHighlightText);

  const shouldPinOfferOnSr =
    flight.highlightInfo?.highlight &&
    ((Boolean(trackExperiment("flights_web_cat_pin_flight_www")) && isOfMetaOrigin()) ||
      isFeatureRunningClientSide("FLIGHTS_WEB_PIN_OFFER_ON_SR_FROM_REDIRECT") ||
      flight.highlightInfo.reason === "CROSS_SELL_OFFER");

  const hasPrefetchedBrandedFareOffers = !!flight.brandedFareOffers && flight.brandedFareOffers?.length > 0;
  const canShowFareSelector =
    boundsVariant !== "outbound" && (flight.requestableBrandedFares || hasPrefetchedBrandedFareOffers);
  const showFareSelectorDefault = canShowFareSelector && displayOptions?.brandedFaresShownByDefault;
  const showCompactFareSelector = !!displayOptions?.brandedFaresShownByDefault;

  /* start- flights_apex_web_compact_bf_on_sr_button */
  const showCompactFareSelectorButton =
    showCompactFareSelector || !!flights_apex_web_compact_bf_on_sr_button.trackWithDefaultStage();
  const viewDetailsRef = useRef<HTMLButtonElement>(null);
  const viewDetailsVisible = useIsInViewport(viewDetailsRef);
  const flights = useSelector(getSearchResultsFlights);
  if (viewDetailsVisible && canShowFareSelector)
    flights_apex_web_compact_bf_on_sr_button.trackSearchResultsStages(flights);
  /* end - flights_apex_web_compact_bf_on_sr_button */

  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [showFareSelector, setShowFareSelector] = useState(showFareSelectorDefault);
  const [showDivider, setShowDivider] = useState(false);
  const [hoveringBadge, setHoveringBadge] = useState(false);
  const hoverTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (hoveringBadge) {
      hoverTimerRef.current = setTimeout(() => {
        flights_apex_web_bf_on_sr_flex_badge.goals.hovers_badge();
      }, 500);
    } else if (hoverTimerRef.current) {
      clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }

    return () => {
      if (hoverTimerRef.current) {
        clearTimeout(hoverTimerRef.current);
      }
    };
  }, [hoveringBadge]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const removeDivider = useCallback(() => setShowDivider(false), []);

  useFetchBrandedFares(
    flight.token,
    isInViewport,
    boundsVariant !== "outbound" && !!flight.requestableBrandedFares,
    true,
    !!flight.brandedFareInfo?.fareName,
    carriers
  );

  const viewDetailsButtonLabel =
    boundsVariant === "outbound" ? t("flights_sr_split_bounds_us_cta_select") : t("flights_apex_sr_flex_cta_main");
  /* start - flights_web_flexible_ticket_icon */

  // The condition here checks whether the card has/isEligible for flex badge/icon and if it's in viewport
  useTrackFlexIcon((!!hasFlexibleTicketBadge || !!showFlexBadgeOnBrandedFare) && isInViewport);
  /* end - flights_web_flexible_ticket_icon */

  const travellersCount = adults + children.length;

  const flightPriceComponent = rebookProps ? (
    <RebookCostBreakdown {...rebookProps} breakdownOpen={breakdownOpen} setBreakdownOpen={setBreakdownOpen} />
  ) : boundsVariant === "outbound" ? (
    <FlightCardPriceOutbound flight={flight} />
  ) : (
    <FlightCardPrice
      flight={flight}
      breakdownOpen={breakdownOpen}
      setBreakdownOpen={setBreakdownOpen}
      showPricePerPerson={!!boundsVariant || (pricePerPerson && adults + children.length > 1)}
      showAvailableSeats={isOfMetaOrigin() && adults + children.length <= 2}
      totalPriceLabel={i18n.trans(
        travellersCount > 1 ? t("flights_sr_rt_price_per") : t("flights_sr_price_round_trip")
      )}
      popoverCloseAriaLabel={i18n.trans(t("flights_a11y_price_breakdown_close_sr"))}
      boundsVariant={boundsVariant}
    />
  );

  const highlightFireflySR =
    isInternal &&
    flight.token.startsWith("c8e49_") &&
    new URLSearchParams(window.location.search).get("highlight_firefly") === "1";

  const toggleShowFareSelector = useCallback(() => {
    if (showFareSelector) {
      flights_apex_web_ancillaries_micro_conversion_aa.goals.show_fare_options_sr();
      flights_apex_web_compact_bf_on_sr_button.goals.collapses_fares();
    } else {
      flights_apex_web_bf_on_sr_loading_message.goals.close_expanded_branded_fares();
      flights_apex_web_compact_bf_on_sr_button.goals.expands_fares();
      flights_apex_web_bf_on_sr_flex_badge.goals.expands_offer();
    }

    setShowFareSelector(!showFareSelector);
  }, [showFareSelector, setShowFareSelector]);

  const toggleFareSelectorIcon = useMemo(
    () => <Icon svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon} attributes={{ "aria-hidden": true }} />,
    [showFareSelector]
  );

  const viewDetailsButton = (
    <Button
      attributes={{
        "data-testid": "flight_card_bound_select_flight",
        "aria-describedby": `flight-card-${index}`,
        "aria-label": `${i18n.trans(viewDetailsButtonLabel)} ${
          hasFlexibleTicketBadge ? i18n.trans(t("flights_apex_sr_flex_badge")) : ""
        }`,
        ref: viewDetailsRef
      }}
      text={i18n.trans(viewDetailsButtonLabel)}
      onClick={() => {
        flights_apex_web_bf_on_sr_loading_message.goals.click_on_view_details_for_main_flight();
        onClick(flight);
      }}
      variant="secondary"
      className={styles.noMarginButton}
    />
  );

  return (
    <div id={`flight-card-${index}`} ref={flightCardBoundRef}>
      <Card
        fill
        className={highlightFireflySR && styles.highlightFirefly}
        variant={shouldPinOfferOnSr ? "elevated" : "neutral"}
      >
        {shouldPinOfferOnSr ? (
          <FlightCardHighlight
            style={FlightCardHighlightStyle.PRIMARY}
            text={flight.highlightInfo?.message || i18n.trans(t("flights_meta_sr_pinned_flight_label"))}
          />
        ) : shouldShowBestOfferHightlight ? (
          <FlightCardHighlight style={bestOfferHighlightStyle} text={bestOfferHighlightText} textEmphasized={true} />
        ) : null}

        <Frame p={4} direction="row" className={showDivider ? styles.divider : undefined}>
          <Frame
            attributes={{
              style: {
                width: "64%"
              }
            }}
            justifyContent="flex-start"
          >
            {showFlightCardBadges && (
              <FlightCardBadges className={styles.badgesWithBottomMargin}>
                {flight?.badges?.map(({ text, variant, type }, idx) => (
                  <FlightCardBadge
                    key={text + idx}
                    text={text}
                    style={variant}
                    alternative
                    type={type}
                    flightToken={flight.token}
                  />
                ))}
                {!flights_web_flexible_ticket_icon.trackWithDefaultStage() &&
                  (hasFlexibleTicketBadge || showFlexBadgeOnBrandedFare) && (
                    <span onMouseEnter={() => setHoveringBadge(true)} onMouseLeave={() => setHoveringBadge(false)}>
                      <FlightCardBadge
                        style={FlightBadgeVariant.CONSTRUCTIVE}
                        alternative={true}
                        text={i18n.trans(t("flights_apex_sr_flex_badge"))}
                      />
                    </span>
                  )}

                {flight.hasAppleCardBenefits && (
                  <FlightCardBadge
                    style={FlightBadgeVariant.CALLOUT}
                    alternative={true}
                    text={i18n.trans(t("apple_rewards_flights_sr_badge_title"))}
                    flightToken={flight.token}
                  />
                )}
              </FlightCardBadges>
            )}
            <BaseFareAttributes flight={flight} />
            <CardSegmentsCondensed segments={flight.segments} bigLogo={bigLogo} />
          </Frame>

          <Frame direction="column" justifyContent={"center"} className={styles.priceBlock}>
            {rebookProps?.tripRebookOption ? (
              <FlightCardLuggage segments={rebookProps.tripRebookOption.segments} />
            ) : (
              <FlightCardLuggageWithCheckmarks
                segments={flight.segments}
                showFareName={canShowFareSelector}
                brandedFareInfo={flight.brandedFareInfo}
                showFlexIcon={
                  !!flights_web_flexible_ticket_icon.trackWithDefaultStage() &&
                  (hasFlexibleTicketBadge || showFlexBadgeOnBrandedFare)
                }
              />
            )}

            {campaignBadges.length > 0 && (
              <FlightCardBadges
                className={mcn(
                  styles.badgesInRightSection,
                  !rebookProps?.tripRebookOption &&
                    styles.badgesWithTopMargin /* The component above doesn't have bottom padding when rebookProps?.tripRebookOption = true. */
                )}
              >
                {campaignBadges.map((badge, idx) => (
                  <FlightCardBadge
                    key={`keyCampaignBadge-${idx}`}
                    alternative={false}
                    isInteractive={false}
                    {...badge}
                    flightToken={flight.token}
                  />
                ))}
              </FlightCardBadges>
            )}

            <div style={{ textAlign: isRTL ? "left" : "right" }}>
              <Frame mt={2}>{flightPriceComponent}</Frame>

              {canShowFareSelector && showCompactFareSelectorButton ? (
                <Stack direction="row" grow>
                  <Stack.Item alignSelf="end">
                    <Button
                      attributes={{
                        "aria-expanded": !!showFareSelector,
                        "aria-controls": `flights-fare-selector-${index}`,
                        "aria-describedby": `flight-card-${index}`,
                        "aria-label": showFareSelector
                          ? i18n.trans(t("a11y_flights_apex_bf_sr_hide_options"))
                          : i18n.trans(t("a11y_flights_apex_bf_sr_expand_options"))
                      }}
                      icon={toggleFareSelectorIcon}
                      variant="secondary"
                      onClick={toggleShowFareSelector}
                      className={styles.showFaresButtonWithMargin}
                    />
                  </Stack.Item>
                  <Stack.Item grow>{viewDetailsButton}</Stack.Item>
                </Stack>
              ) : (
                <>
                  {viewDetailsButton}
                  {canShowFareSelector ? (
                    <Button
                      attributes={{
                        "aria-expanded": !!showFareSelector,
                        "aria-controls": `flights-fare-selector-${index}`,

                        "aria-describedby": `flight-card-${index}`
                      }}
                      text={
                        showFareSelector
                          ? i18n.trans(t("flights_apex_sr_flex_cta_hide"))
                          : i18n.trans(t("flights_apex_sr_flex_cta_explore"))
                      }
                      onClick={() => {
                        toggleShowFareSelector();
                        flights_apex_web_bf_on_sr_flex_badge.goals.expands_offer();
                      }}
                      variant="tertiary"
                      iconPosition="end"
                      wide={true}
                      icon={
                        <Icon
                          svg={showFareSelector ? ArrowNavUpIcon : ArrowNavDownIcon}
                          attributes={{ "aria-hidden": true }}
                        />
                      }
                      className={styles.showFaresButtonWithMargin}
                    />
                  ) : null}
                </>
              )}
            </div>
          </Frame>
        </Frame>
        {canShowFareSelector && (
          <div id={`flights-fare-selector-${index}`}>
            {showFareSelector && <FareSelectorDesktop flight={flight} isCompact={showCompactFareSelector} />}
          </div>
        )}
      </Card>
    </div>
  );
};

const FlightCardDesktop = React.memo(FlightCardBound);
export default FlightCardDesktop;
